<template>
    <div class="ComponentPlayground" :class="{ 'ComponentPlayground--resizing': isResizing }">
        <div v-if="title || description" class="ComponentPlayground__header">
            <h2 v-if="title" class="ComponentPlayground__title">
                {{ title }}
            </h2>
            <p v-if="description" class="ComponentPlayground__desc">
                {{ description }}
            </p>
        </div>
        <div class="ComponentPlayground__body">
            <!-- Preview on the left -->
            <div class="ComponentPlayground__preview">
                <div class="ComponentPlayground__previewContent">
                    <slot :live-props="liveProps">
                        <component :is="component" v-bind="liveProps" v-on="computedListeners" />
                    </slot>
                </div>
                <div v-if="eventLog.length > 0" class="ComponentPlayground__events">
                    <div class="ComponentPlayground__eventsHeading">
                        Events
                    </div>
                    <div
                        v-for="(entry, i) in eventLog"
                        :key="i"
                        class="ComponentPlayground__eventEntry"
                    >
                        <span class="ComponentPlayground__eventName">@{{ entry.name }}</span>
                        <span class="ComponentPlayground__eventValue">{{ entry.valueStr }}</span>
                    </div>
                </div>
            </div>

            <!-- Drag handle -->
            <div class="ComponentPlayground__divider" @mousedown.prevent="startResize" />

            <!-- Props panel on the right -->
            <aside class="ComponentPlayground__sidebar" :style="{ width: sidebarWidth + 'px' }">
                <div class="ComponentPlayground__sidebarHeading">
                    Props
                </div>
                <div
                    v-for="field in fields"
                    :key="field.name"
                    class="ComponentPlayground__field"
                >
                    <div class="ComponentPlayground__fieldHeader">
                        <code class="ComponentPlayground__fieldName">{{ field.name }}</code>
                        <span class="ComponentPlayground__fieldType">{{ field.typeLabel }}</span>
                    </div>
                    <label v-if="field.inputType === 'checkbox'" class="ComponentPlayground__checkRow">
                        <input
                            type="checkbox"
                            :checked="liveProps[field.name]"
                            @change="setLiveProp(field.name, $event.target.checked)"
                        >
                        <span>{{ liveProps[field.name] ? 'true' : 'false' }}</span>
                    </label>
                    <input
                        v-else-if="field.inputType === 'number'"
                        class="ComponentPlayground__input"
                        type="number"
                        :value="liveProps[field.name]"
                        @input="setLiveProp(
                            field.name,
                            $event.target.value === '' ? null : Number($event.target.value)
                        )"
                    >
                    <input
                        v-else-if="field.inputType === 'text'"
                        class="ComponentPlayground__input"
                        type="text"
                        :value="liveProps[field.name]"
                        @input="setLiveProp(field.name, $event.target.value)"
                    >
                    <div v-else class="ComponentPlayground__jsonWrap">
                        <textarea
                            class="ComponentPlayground__textarea"
                            :class="{ 'ComponentPlayground__textarea--error': !!jsonErrors[field.name] }"
                            :value="jsonStrings[field.name]"
                            @input="onJsonInput(field.name, $event.target.value)"
                            @blur="commitJson(field.name)"
                        />
                        <span v-if="jsonErrors[field.name]" class="ComponentPlayground__jsonError">
                            {{ jsonErrors[field.name] }}
                        </span>
                    </div>
                </div>
                <template v-if="slotNames.length > 0">
                    <div class="ComponentPlayground__sidebarHeading ComponentPlayground__sectionSep">
                        Slots
                    </div>
                    <div
                        v-for="slotName in slotNames"
                        :key="slotName"
                        class="ComponentPlayground__field"
                    >
                        <div class="ComponentPlayground__fieldHeader">
                            <code class="ComponentPlayground__fieldName">#{{ slotName }}</code>
                            <span class="ComponentPlayground__fieldType">slot</span>
                        </div>
                    </div>
                </template>
            </aside>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.ComponentPlayground {
    background: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &--resizing {
        user-select: none;
    }

    &__header {
        padding: 1.25rem 1.5rem 0;
    }

    &__title {
        margin: 0 0 0.25rem;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--eidTechGray);
    }

    &__desc {
        margin: 0;
        font-size: 0.8125rem;
        color: var(--eidTechGray-75);
    }

    &__body {
        display: flex;
        margin-top: 1rem;
        min-height: 180px;
        border-radius: 0 0 0.5rem 0.5rem;
        overflow: hidden;
    }

    &__preview {
        flex: 1;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        min-width: 0;
    }

    &__previewContent {
        width: 100%;
        min-width: 200px;
    }

    &__events {
        border-top: 1px solid var(--eidTechGray-10);
        padding-top: 0.75rem;
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
    }

    &__eventsHeading {
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--eidTechGray-50);
        margin-bottom: 0.125rem;
    }

    &__eventEntry {
        display: flex;
        gap: 0.5rem;
        font-size: 0.75rem;
        font-family: monospace;
    }

    &__eventName {
        color: var(--eidSecureBlue);
        white-space: nowrap;
        flex-shrink: 0;
    }

    &__eventValue {
        color: var(--eidTechGray-75);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__divider {
        width: 5px;
        flex-shrink: 0;
        background: var(--eidTechGray-10);
        border-left: 1px solid rgba(0, 0, 0, 0.06);
        cursor: col-resize;
        transition: background 0.15s;

        &:hover {
            background: var(--eidTechGray-25);
        }
    }

    &__sidebar {
        width: 280px;
        flex-shrink: 0;
        padding: 1rem;
        background: var(--eidTechGray-10);
        display: flex;
        flex-direction: column;
        gap: 0.875rem;
        overflow-y: auto;
        max-height: 250px;
    }

    &__sidebarHeading {
        font-size: 0.6875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--eidTechGray-50);

        &.ComponentPlayground__sectionSep {
            margin-top: 0.25rem;
            padding-top: 0.875rem;
            border-top: 1px solid rgba(0, 0, 0, 0.1);
        }
    }

    &__field {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
    }

    &__fieldHeader {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
    }

    &__fieldName {
        font-size: 0.8125rem;
        font-weight: 500;
        color: var(--eidTechGray);
        font-family: monospace;
        background: none;
        padding: 0;
    }

    &__fieldType {
        font-size: 0.6875rem;
        color: var(--eidTechGray-50);
        font-family: monospace;
        white-space: nowrap;
    }

    &__checkRow {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        font-size: 0.8125rem;
        color: var(--eidTechGray-75);
        cursor: pointer;
        user-select: none;
    }

    &__input {
        width: 100%;
        padding: 0.3rem 0.5rem;
        font-size: 0.8125rem;
        border: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: 0.25rem;
        background: #fff;
        color: var(--eidTechGray);
        box-sizing: border-box;
    }

    &__jsonWrap {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
    }

    &__textarea {
        width: 100%;
        height: 80px;
        padding: 0.3rem 0.5rem;
        font-size: 0.6875rem;
        font-family: monospace;
        border: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: 0.25rem;
        background: #fff;
        color: var(--eidTechGray);
        resize: vertical;
        box-sizing: border-box;

        &--error {
            border-color: var(--eidDangerRed);
        }
    }

    &__jsonError {
        font-size: 0.6875rem;
        color: var(--eidDangerRed);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    type InputType = 'checkbox' | 'number' | 'text' | 'json';

    interface Field {
        name: string;
        inputType: InputType;
        typeLabel: string;
    }

    interface EventLogEntry {
        name: string;
        valueStr: string;
    }

    function getPrimaryConstructor(propDef: any): Function | null {
        if (!propDef) {
            return null;
        }
        if (typeof propDef === 'function') {
            return propDef;
        }
        if (Array.isArray(propDef)) {
            return propDef[0] ?? null;
        }
        if (propDef.type) {
            const t = propDef.type;
            if (typeof t === 'function') {
                return t;
            }
            if (Array.isArray(t)) {
                return t[0] ?? null;
            }
        }
        return null;
    }

    function inferInputTypeFromValue(value: any): InputType {
        if (typeof value === 'boolean') {
            return 'checkbox';
        }
        if (typeof value === 'number') {
            return 'number';
        }
        if (typeof value === 'string') {
            return 'text';
        }
        return 'json';
    }

    function resolveInputType(propDef: any, fallbackValue?: any): InputType {
        const ctor = getPrimaryConstructor(propDef);
        if (ctor === Boolean) {
            return 'checkbox';
        }
        if (ctor === Number) {
            return 'number';
        }
        if (ctor === String) {
            return 'text';
        }
        if (ctor !== null) {
            return 'json';
        }
        return inferInputTypeFromValue(fallbackValue);
    }

    function resolveTypeLabel(propDef: any, fallbackValue?: any): string {
        const toName = (fn: Function) => (fn?.name ?? 'any').toLowerCase();
        if (!propDef) {
            return typeof fallbackValue === 'boolean'
                ? 'boolean'
                : typeof fallbackValue === 'number'
                    ? 'number'
                    : typeof fallbackValue === 'string'
                        ? 'string'
                        : 'any';
        }
        if (typeof propDef === 'function') {
            return toName(propDef as Function);
        }
        if (Array.isArray(propDef)) {
            return (propDef as Function[]).map(toName).join(' | ');
        }
        if (propDef.type) {
            const t = propDef.type;
            if (typeof t === 'function') {
                return toName(t as Function);
            }
            if (Array.isArray(t)) {
                return (t as Function[]).map(toName).join(' | ');
            }
        }
        return 'any';
    }

    function getComponentPropDefs(component: any): Record<string, any> {
        const opts = typeof component === 'function' ? component.options : component;
        return opts?.props ?? {};
    }

    function getComponentSlotNames(component: any): string[] {
        const opts = typeof component === 'function' ? component.options : component;
        const render: Function | undefined = opts?.render;
        if (!render) {
            return [];
        }
        const src = render.toString();
        const names: string[] = [];
        const seen = new Set<string>();
        const re = /_t\("([^"]+)"\)/g;
        let match = re.exec(src);
        while (match !== null) {
            if (!seen.has(match[1])) {
                seen.add(match[1]);
                names.push(match[1]);
            }
            match = re.exec(src);
        }
        return names;
    }

    const MIN_SIDEBAR_WIDTH = 200;
    const MAX_SIDEBAR_WIDTH = 600;

    export default defineComponent({
        name: 'ComponentPlayground',
        props: {
            component: {
                type: [ Object, Function ] as PropType<any>,
                required: true,
            },
            defaults: {
                type: Object as PropType<Record<string, any>>,
                required: true,
            },
            title: {
                type: String as PropType<string>,
                default: '',
            },
            description: {
                type: String as PropType<string>,
                default: '',
            },
            eventBindings: {
                type: Object as PropType<Record<string, string>>,
                default: (): Record<string, string> => ({}),
            },
            eventTransformers: {
                type: Object as PropType<Record<string, (value: any) => any>>,
                default: (): Record<string, (value: any) => any> => ({}),
            },
            propCustomTypeLabels: {
                type: Object as PropType<Record<string, string>>,
                default: (): Record<string, string> => ({}),
            },
        },
        data() {
            const propDefs = getComponentPropDefs(this.component);
            const liveProps: Record<string, any> = { ...this.defaults };
            const jsonStrings: Record<string, string> = {};
            const jsonErrors: Record<string, string> = {};

            for (const key of Object.keys(liveProps)) {
                if (resolveInputType(propDefs[key], liveProps[key]) === 'json') {
                    const val = liveProps[key];
                    jsonStrings[key] = val == null ? '' : JSON.stringify(val, null, 2);
                    jsonErrors[key] = '';
                }
            }

            return {
                liveProps,
                jsonStrings,
                jsonErrors,
                eventLog: [] as EventLogEntry[],
                sidebarWidth: 280,
                isResizing: false,
                resizeStartX: 0,
                resizeStartWidth: 0,
            };
        },
        computed: {
            slotNames(): string[] {
                return getComponentSlotNames(this.component);
            },
            fields(): Field[] {
                const propDefs = getComponentPropDefs(this.component);
                return Object.keys(this.defaults).map((name) => {
                    const propDef = propDefs[name];
                    const fallback = this.defaults[name];
                    return {
                        name,
                        inputType: resolveInputType(propDef, fallback),
                        typeLabel: (this.propCustomTypeLabels as Record<string, string>)[name]
                            ?? resolveTypeLabel(propDef, fallback),
                    };
                });
            },
            computedListeners(): Record<string, (value: any) => void> {
                const result: Record<string, (value: any) => void> = {};
                for (const eventName of Object.keys(this.eventBindings)) {
                    result[eventName] = (value: any) => this.handleEvent(eventName, value);
                }
                return result;
            },
        },
        beforeUnmount() {
            this.stopResize();
        },
        methods: {
            setLiveProp(name: string, value: any): void {
                this.liveProps[name] = value;
            },
            onJsonInput(name: string, value: string): void {
                this.jsonStrings[name] = value;
            },
            commitJson(fieldName: string): void {
                const raw = this.jsonStrings[fieldName] ?? '';
                try {
                    this.liveProps[fieldName] = JSON.parse(raw);
                    this.jsonErrors[fieldName] = '';
                } catch {
                    this.jsonErrors[fieldName] = 'Invalid JSON';
                }
            },
            handleEvent(eventName: string, value: any): void {
                const transformer = (this.eventTransformers as Record<string, (v: any) => any>)[eventName];
                const finalValue = transformer ? transformer(value) : value;
                const propName = (this.eventBindings as Record<string, string>)[eventName];
                if (propName !== undefined) {
                    this.liveProps[propName] = finalValue;
                    if (finalValue !== null && typeof finalValue === 'object') {
                        this.jsonStrings[propName] = JSON.stringify(finalValue, null, 2);
                    } else if (finalValue == null) {
                        this.jsonStrings[propName] = '';
                    }
                }
                this.addToEventLog(eventName, finalValue);
            },
            addToEventLog(name: string, value: any): void {
                let valueStr: string;
                try {
                    valueStr = JSON.stringify(value);
                } catch {
                    valueStr = String(value);
                }
                this.eventLog.unshift({ name, valueStr });
                if (this.eventLog.length > 5) {
                    this.eventLog.pop();
                }
            },
            startResize(e: MouseEvent): void {
                this.isResizing = true;
                this.resizeStartX = e.clientX;
                this.resizeStartWidth = this.sidebarWidth;
                window.addEventListener('mousemove', this.onResize);
                window.addEventListener('mouseup', this.stopResize);
            },
            onResize(e: MouseEvent): void {
                if (!this.isResizing) {
                    return;
                }
                // Sidebar is on the right — dragging left increases its width
                const delta = this.resizeStartX - e.clientX;
                this.sidebarWidth = Math.max(
                    MIN_SIDEBAR_WIDTH,
                    Math.min(MAX_SIDEBAR_WIDTH, this.resizeStartWidth + delta),
                );
            },
            stopResize(): void {
                if (!this.isResizing) {
                    return;
                }
                this.isResizing = false;
                window.removeEventListener('mousemove', this.onResize);
                window.removeEventListener('mouseup', this.stopResize);
            },
        },
    });
</script>
